"use client";
import { Button, Flex, FlexProps, FormControl } from "@chakra-ui/react";
import { useState } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { Card } from "../../../../components/card/card";
import { CardHeader } from "../../../../components/card/card-header";
import { SingleFormField, UploadAvatar, UploadAvatarProps } from "../common";

export interface User_T {
  firstName: string;
  lastName: string;
  email: string;
}
export interface BasicInfoFormProps extends FlexProps {
  user: User_T;

  onEditClick?: () => void;
  onSubmit: SubmitHandler<FieldValues>;
  avatar: UploadAvatarProps;
}
export const BasicInfoForm = (props: BasicInfoFormProps) => {
  const { user, onSubmit, avatar, onEditClick, ...rest } = props;
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
  } = useForm();

  const [canEdit, setCanEdit] = useState(false);

  const nameInputs = [
    { htmlFor: "firstName", label: "First Name", type: "text" },
    { htmlFor: "lastName", label: "Last Name", type: "text" },
  ];

  const formFieldStyles = {
    labelSx: { fontSize: "xs", m: "0", fontWeight: "medium" },
    inputSx: {
      p: { base: "3", md: "6" },
      borderColor: "gray.700",
      fontWeight: "normal",
    },
  };

  const buttonStyles = {
    mt: "4",
    colorScheme: "green",
    width: { base: "xs", md: "3xs" },
  };
  return (
    <Flex
      direction="column"
      align={{ base: "center", md: "flex-end" }}
      {...rest}
    >
      <Card>
        <CardHeader title="Basic Info" textColor="gray.700" />
        <Flex
          direction={{ base: "column", md: "row" }}
          bg="white"
          align="center"
          justify="center"
          gap={{ base: "6", md: "16", lg: "28" }}
          p="5"
          mx="5"
        >
          <UploadAvatar {...avatar} />
          <Flex
            as="form"
            id="basic-info-form"
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
              setCanEdit(false);
            }}
            bg="white"
            textColor="gray.700"
            p="5"
            gap="4"
            w={{ base: "xs", md: "lg", lg: "2xl", xl: "3xl" }}
            direction="column"
          >
            <Flex
              as={FormControl}
              direction="column"
              gap="4"
              isReadOnly={!canEdit}
              isRequired={canEdit ? true : false}
            >
              <Flex
                justify="flex-start"
                gap="6"
                direction={{ base: "column", md: "row" }}
              >
                {nameInputs.map((input, idx) => (
                  <SingleFormField
                    key={idx}
                    {...input}
                    defaultValue={user[input.htmlFor as keyof User_T]}
                    register={register(input.htmlFor, {
                      minLength: 1,
                    })}
                    {...formFieldStyles}
                  />
                ))}
              </Flex>
              <SingleFormField
                htmlFor="email"
                label="Email"
                type="email"
                defaultValue={user.email}
                register={register("email", {
                  minLength: 1,
                })}
                {...formFieldStyles}
              />
            </Flex>
          </Flex>
        </Flex>
      </Card>
      {canEdit ? (
        <Button
          isLoading={isSubmitting}
          type="submit"
          form="basic-info-form"
          {...buttonStyles}
        >
          Save
        </Button>
      ) : (
        <Button
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onEditClick && onEditClick();
            setCanEdit(true);
          }}
          {...buttonStyles}
        >
          Edit
        </Button>
      )}
    </Flex>
  );
};
